/**
 * Canonical payment-method mapping for cashier reporting.
 *
 * Historical invoice_payments.payment_method values are free-text. This layer
 * never writes back to stored rows — it only normalizes labels for grouping.
 */

export const CANONICAL_PAYMENT_METHODS = [
  'clinio_pay',
  'card',
  'cash',
  'bank_transfer',
  'mobilepay',
  'invoice',
  'other',
] as const;

export type KnownPaymentMethod = (typeof CANONICAL_PAYMENT_METHODS)[number];

export type PaymentChannel = 'clinio_pay' | 'other';

export const PAYMENT_METHOD_LABEL_KEYS: Record<KnownPaymentMethod, string> = {
  clinio_pay: 'reports.cashier.methods.clinio_pay',
  card: 'reports.cashier.methods.card',
  cash: 'reports.cashier.methods.cash',
  bank_transfer: 'reports.cashier.methods.bank_transfer',
  mobilepay: 'reports.cashier.methods.mobilepay',
  invoice: 'reports.cashier.methods.invoice',
  other: 'reports.cashier.methods.other',
};

const KNOWN = new Set<string>(CANONICAL_PAYMENT_METHODS);

/** Aliases after foldKey(). `refund` is a sentinel: inherit the original method. */
const ALIASES: Record<string, KnownPaymentMethod | 'inherit'> = {
  clinio_pay: 'clinio_pay',
  cliniopay: 'clinio_pay',
  'clinio pay': 'clinio_pay',
  stripe: 'clinio_pay',
  card: 'card',
  kort: 'card',
  creditcard: 'card',
  'credit card': 'card',
  debitcard: 'card',
  'debit card': 'card',
  dankort: 'card',
  visa: 'card',
  mastercard: 'card',
  cash: 'cash',
  kontant: 'cash',
  bank_transfer: 'bank_transfer',
  banktransfer: 'bank_transfer',
  'bank transfer': 'bank_transfer',
  bank: 'bank_transfer',
  bankoverforsel: 'bank_transfer',
  bankoverfoersel: 'bank_transfer',
  bankovf: 'bank_transfer',
  'bank ovf': 'bank_transfer',
  overforsel: 'bank_transfer',
  overfoersel: 'bank_transfer',
  wire: 'bank_transfer',
  'wire transfer': 'bank_transfer',
  mobilepay: 'mobilepay',
  'mobile pay': 'mobilepay',
  invoice: 'invoice',
  faktura: 'invoice',
  'invoice payment': 'invoice',
  other: 'other',
  andet: 'other',
  misc: 'other',
  unknown: 'other',
  refund: 'inherit',
  refusion: 'inherit',
  refunded: 'inherit',
};

function foldKey(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function slug(value: string): string {
  const folded = foldKey(value).replace(/\s+/g, '_');
  return folded || 'other';
}

export function isKnownPaymentMethod(value: string): value is KnownPaymentMethod {
  return KNOWN.has(value);
}

/**
 * Canonical grouping key for a stored payment-method label.
 * Returns `inherit` when the stored value means "this row is a refund".
 */
export function normalizePaymentMethodKey(raw: string | null | undefined): string | 'inherit' {
  if (raw == null) return 'other';
  const trimmed = raw.trim();
  if (!trimmed) return 'other';
  const folded = foldKey(trimmed);
  if (!folded) return 'other';
  const compact = folded.replace(/\s+/g, '');
  const aliased = ALIASES[folded] ?? ALIASES[compact];
  if (aliased) return aliased;
  return slug(trimmed);
}

export function paymentChannelForMethod(methodKey: string): PaymentChannel {
  return methodKey === 'clinio_pay' ? 'clinio_pay' : 'other';
}

export function humanizePaymentMethod(raw: string): string {
  const cleaned = raw.trim().replace(/\s+/g, ' ');
  if (!cleaned) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function resolvePaymentMethodKey(
  raw: string | null | undefined,
  fallbackRaw?: string | null,
): string {
  const key = normalizePaymentMethodKey(raw);
  if (key !== 'inherit') return key;
  const fallback = normalizePaymentMethodKey(fallbackRaw);
  return fallback === 'inherit' ? 'other' : fallback;
}

export function methodSortIndex(methodKey: string): number {
  const index = CANONICAL_PAYMENT_METHODS.indexOf(methodKey as KnownPaymentMethod);
  if (index >= 0 && methodKey !== 'other') return index;
  if (methodKey === 'other') return 1000;
  return 100 + methodKey.charCodeAt(0);
}
